import { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
} from "reactstrap";

import {
    FiX,
} from "react-icons/fi";

import "./PlacementPopup.css";


const defaultPlacements = [
    {
        image: "/placements/Altron.jpe",
        company: "Altron",
    },
    {
        image: "/placements/ASM%20Tech.webp",
        company: "ASM Tech",
    },
    {
        image: "/placements/autodesk%20naveen.webp",
        company: "Autodesk",
    },
    {
        image: "/placements/Bhavani-Shankar.jpe",
        company: "Kennametal",
    },
    {
        image: "/placements/Donaldson.webp",
        company: "Donaldson",
    },
    {
        image: "/placements/Ford.webp",
        company: "Ford",
    },
    {
        image: "/placements/Haldor%20Topsoe.webp",
        company: "Haldor Topsoe",
    },
    {
        image: "/placements/Icube%20logics.jpe",
        company: "iCube Logics",
    },
    {
        image: "/placements/ITC%20Infotech.webp",
        company: "ITC Infotech",
    },
    {
        image: "/placements/Prasanna-Donaldson.webp",
        company: "Donaldson",
    },
    {
        image: "/placements/TechMahindra.webp",
        company: "Tech Mahindra",
    },
    {
        image: "/placements/VivekBalakrishnan-TATA.jpe",
        company: "TATA",
    },
];


function PlacementPopup({
    isOpen,
    toggle,
    placements = defaultPlacements,
}) {

    /*
     * =========================================================
     * CARD OPEN DELAY
     * =========================================================
     *
     * 1000 = 1 second
     * 1500 = 1.5 seconds
     * 2000 = 2 seconds
     */
 


    const [cardsVisible, setCardsVisible] = useState(false);


    /*
     * =========================================================
     * PRELOAD + DECODE IMAGES
     * =========================================================
     *
     * Images are loaded BEFORE cards are revealed.
     *
     * This prevents:
     *
     * CARD → blank image → image appears
     *
     * Instead:
     *
     * WAIT → CARD + IMAGE TOGETHER
     * =========================================================
     */

    useEffect(() => {

        if (!isOpen || !placements?.length) {
            return;
        }


        let cancelled = false;


        const preloadImages = async () => {

            try {

                const imagePromises = placements.map(
                    (placement) => {

                        return new Promise((resolve) => {

                            const img = new Image();

                            img.src = placement.image;


                            /*
                             * If image is already cached
                             */

                            if (img.complete) {

                                if (img.decode) {

                                    img.decode()
                                        .catch(() => {})
                                        .finally(resolve);

                                } else {

                                    resolve();

                                }

                                return;
                            }


                            /*
                             * Image loaded
                             */

                            img.onload = async () => {

                                if (img.decode) {

                                    try {

                                        await img.decode();

                                    } catch {
                                        // Ignore decode error
                                    }

                                }

                                resolve();

                            };


                            /*
                             * Don't block forever
                             */

                            img.onerror = () => {

                                resolve();

                            };

                        });

                    }
                );


                /*
                 * Wait until all images are ready
                 */

                await Promise.all(imagePromises);


                if (cancelled) {
                    return;
                }


                /*
                 * IMPORTANT:
                 *
                 * Even if images load quickly,
                 * wait the configured 1.5 seconds.
                 */

                setTimeout(() => {

                    if (!cancelled) {

                        setCardsVisible(true);

                    }

                },  );

            } catch {

                /*
                 * Fallback
                 */

                if (!cancelled) {

                    setTimeout(() => {

                        setCardsVisible(true);

                    },  );

                }

            }

        };


        setCardsVisible(false);

        preloadImages();


        return () => {

            cancelled = true;

        };

    }, [isOpen, placements]);


    if (!placements || placements.length === 0) {
        return null;
    }


    /*
     * =========================================================
     * DUPLICATE PLACEMENTS
     * =========================================================
     */

    const scrollingPlacements = [
        ...placements,
        ...placements,
    ];


    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="xl"
            className="placement-popup-modal"
        >

            <ModalBody className="placement-popup-body">


                {/* =================================================
                    CLOSE
                ================================================= */}

                <Button
                    className="placement-popup-close"
                    onClick={toggle}
                    aria-label="Close placement popup"
                >

                    <FiX />

                </Button>


                {/* =================================================
                    HEADER
                ================================================= */}

                <div className="placement-popup-header">

                    <span className="placement-popup-eyebrow">
                        SUCCESS STORIES
                    </span>


                    <h2>
                        Where Our Students Are Now
                    </h2>


                    <p>
                        A proven track record of transforming CAD
                        Engineers into high-paying CAD Automation &
                        Customization professionals at leading companies.
                    </p>

                </div>


                {/* =================================================
                    SLIDER
                ================================================= */}

                <div
                    className={`
                        placement-popup-slider
                        ${
                            cardsVisible
                                ? "placement-slider-visible"
                                : "placement-slider-hidden"
                        }
                    `}
                >

                    <div className="placement-popup-track">

                        {scrollingPlacements.map(
                            (placement, index) => (

                                <div
                                    className="placement-popup-card"
                                    key={`${placement.company}-${index}`}
                                >

                                    <div className="placement-popup-image-wrapper">

                                        <img
                                            src={placement.image}
                                            alt={`${placement.company} placement`}
                                            className="placement-popup-image"

                                            /*
                                             * Do NOT lazy load
                                             */

                                            loading="eager"

                                            /*
                                             * Browser priority
                                             */

                                            fetchPriority="high"
                                        />

                                    </div>


                                    <div className="placement-popup-company">

                                        {placement.company}

                                    </div>

                                </div>

                            )
                        )}

                    </div>

                </div>

            </ModalBody>

        </Modal>
    );
}


export default PlacementPopup;